import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow]
 *
 * @extends {Component<Refs>}
 */
export class BenefitsUseCases extends Component {
  requiredRefs = ['slideshow'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.#sync(this.refs.slideshow?.current ?? 0);
  }

  disconnectedCallback() {
    this.refs.slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    super.disconnectedCallback();
  }

  /**
   * @param {number | string} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();

    const targetIndex = Number.parseInt(`${index}`, 10);
    if (Number.isNaN(targetIndex)) return;

    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    this.#sync(targetIndex);
  }

  #handleSlideshowSelect = (event) => {
    this.#sync(event.detail.index);
  };

  /**
   * @param {number} index
   */
  #sync(index) {
    const items = this.#desktopItems;
    const activeItem = items[index];

    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.toggleAttribute('data-active', isActive);
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', `${isActive}`);

      const trigger = item.querySelector('.benefits-use-cases__desktop-trigger');
      if (trigger instanceof HTMLButtonElement) {
        trigger.setAttribute('aria-pressed', `${isActive}`);
      }
    });

    if (!(activeItem instanceof HTMLElement)) return;

    const title = activeItem.dataset.title || '';
    const description = activeItem.querySelector('.benefits-use-cases__desktop-description')?.innerHTML || '';
    const padLength = Math.max(2, `${items.length}`.length);
    const currentLabel = `${index + 1}`.padStart(padLength, '0') + '.';

    const mobileTitle = this.refs.slideshow?.querySelector('.benefits-use-cases__mobile-title');
    const mobileDescription = this.refs.slideshow?.querySelector('.benefits-use-cases__mobile-description');
    const mobileIndex = this.refs.slideshow?.querySelector('.benefits-use-cases__mobile-index');
    const progressFill = this.refs.slideshow?.querySelector('.benefits-use-cases__mobile-progress-fill');

    if (mobileTitle instanceof HTMLElement) {
      mobileTitle.textContent = title;
    }

    if (mobileDescription instanceof HTMLElement) {
      mobileDescription.innerHTML = description;
    }

    if (mobileIndex instanceof HTMLElement) {
      mobileIndex.textContent = currentLabel;
    }

    if (progressFill instanceof HTMLElement) {
      const progress = items.length <= 1 ? 100 : ((index + 1) / items.length) * 100;
      progressFill.style.width = `${progress}%`;
    }
  }

  get #desktopItems() {
    return [...this.querySelectorAll('.benefits-use-cases__desktop-item')];
  }
}

if (!customElements.get('benefits-use-cases')) {
  customElements.define('benefits-use-cases', BenefitsUseCases);
}
